import { AlertTriangle, Code, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { applySegmentChanges, extractTextSegments, type TextSegment } from '@/lib/htmlTextExtractor';
import { cn } from '@/lib/utils';
import { previewTemplate, testSendTemplate, updateTemplate } from '@/services/notificationTemplateService';

import { ContentEditor } from './ContentEditor';
import { TemplatePreview } from './TemplatePreview';
import { TestSendDialog } from './TestSendDialog';
import { VariablesPanel } from './VariablesPanel';

import type { NotificationTemplate, NotificationTemplatePreview } from '@/types/notificationTemplate.types';

interface TemplateEditorModalProps {
  template: NotificationTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TemplateEditorModal({ template, onClose, onSaved }: TemplateEditorModalProps) {
  const { t } = useTranslation();

  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [textBody, setTextBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [editorTab, setEditorTab] = useState<'content' | 'text'>('content');
  const [isHtmlSourceMode, setIsHtmlSourceMode] = useState(false);

  // Content editor state
  const [segments, setSegments] = useState<TextSegment[]>([]);
  const [segmentEdits, setSegmentEdits] = useState<Record<number, string>>({});

  const [preview, setPreview] = useState<NotificationTemplatePreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestSendOpen, setIsTestSendOpen] = useState(false);
  const [isTestSending, setIsTestSending] = useState(false);

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      const html = template.htmlBody ?? '';
      setSubject(template.subject ?? '');
      setHtmlBody(html);
      setTextBody(template.textBody);
      setIsActive(template.isActive);
      setSegments(extractTextSegments(html));
      setSegmentEdits({});
      setPreview(null);
      setEditorTab('content');
      setIsHtmlSourceMode(false);
    }
  }, [template]);

  // Compute current HTML body with segment edits applied
  const currentHtmlBody = useMemo(() => {
    if (isHtmlSourceMode) return htmlBody;
    const hasEdits = Object.keys(segmentEdits).length > 0;
    if (!hasEdits) return htmlBody;
    return applySegmentChanges(htmlBody, segments, segmentEdits);
  }, [isHtmlSourceMode, htmlBody, segments, segmentEdits]);

  const handleSegmentChange = useCallback((segmentId: number, content: string) => {
    setSegmentEdits((prev) => ({ ...prev, [segmentId]: content }));
  }, []);

  const handleShowHtmlSource = useCallback(() => {
    // Apply pending segment edits to htmlBody before showing raw HTML
    const hasEdits = Object.keys(segmentEdits).length > 0;
    if (hasEdits) {
      const updated = applySegmentChanges(htmlBody, segments, segmentEdits);
      setHtmlBody(updated);
      setSegments(extractTextSegments(updated));
      setSegmentEdits({});
    }
    setIsHtmlSourceMode(true);
  }, [htmlBody, segments, segmentEdits]);

  const handleHideHtmlSource = useCallback(() => {
    // Re-extract segments from possibly edited HTML
    setSegments(extractTextSegments(htmlBody));
    setSegmentEdits({});
    setIsHtmlSourceMode(false);
  }, [htmlBody]);

  const sampleVariables = useCallback((): Record<string, string> => {
    if (!template) return {};
    const vars: Record<string, string> = {};
    for (const v of template.variables) {
      vars[v.name] = v.sampleValue;
    }
    return vars;
  }, [template]);

  const handlePreview = useCallback(async () => {
    if (!template) return;
    setIsPreviewLoading(true);
    try {
      const result = await previewTemplate(template.templateId, {
        variables: sampleVariables(),
        htmlBody: currentHtmlBody,
        textBody,
      });
      setPreview(result);
    } catch {
      toast.error(t('notificationTemplates.toasts.previewError'));
    } finally {
      setIsPreviewLoading(false);
    }
  }, [template, currentHtmlBody, textBody, sampleVariables, t]);

  const handleSave = useCallback(async () => {
    if (!template) return;
    setIsSaving(true);
    try {
      await updateTemplate(template.templateId, {
        subject: subject ?? undefined,
        htmlBody: currentHtmlBody ?? undefined,
        textBody,
        isActive,
      });
      toast.success(t('notificationTemplates.toasts.updateSuccess'));
      onSaved();
    } catch {
      toast.error(t('notificationTemplates.toasts.updateError'));
    } finally {
      setIsSaving(false);
    }
  }, [template, subject, currentHtmlBody, textBody, isActive, onSaved, t]);

  const handleTestSend = useCallback(
    async (email: string) => {
      if (!template) return;
      setIsTestSending(true);
      try {
        await testSendTemplate(template.templateId, {
          recipientEmail: email,
          variables: sampleVariables(),
        });
        toast.success(t('notificationTemplates.toasts.testSendSuccess'));
        setIsTestSendOpen(false);
      } catch {
        toast.error(t('notificationTemplates.toasts.testSendError'));
      } finally {
        setIsTestSending(false);
      }
    },
    [template, sampleVariables, t],
  );

  if (!template) return null;

  return (
    <div className='fixed inset-0 z-50 !mt-0 flex flex-col bg-white'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
        <div className='flex items-center gap-4'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>{t('notificationTemplates.editor.title')}</h2>
            <p className='text-sm text-gray-500'>{template.name}</p>
          </div>
          <span className='inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800'>
            {template.language.toUpperCase()}
          </span>
        </div>
        <button onClick={onClose} className='p-2 text-gray-400 hover:text-gray-600 rounded'>
          <X className='h-5 w-5' />
        </button>
      </div>

      {/* Body — two columns */}
      <div className='flex flex-1 overflow-hidden'>
        {/* Left — Editor */}
        <div className='flex-1 overflow-y-auto border-r border-gray-200 p-6 space-y-4'>
          {/* Subject */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('notificationTemplates.editor.subject')}
            </label>
            <input
              type='text'
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('notificationTemplates.editor.subjectPlaceholder')}
              className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'
            />
          </div>

          {/* Editor tabs */}
          <div>
            <div className='flex items-center justify-between mb-2'>
              <div className='flex gap-1'>
                <button
                  onClick={() => setEditorTab('content')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded',
                    editorTab === 'content' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700',
                  )}>
                  {t('notificationTemplates.editor.emailContentTab')}
                </button>
                <button
                  onClick={() => setEditorTab('text')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded',
                    editorTab === 'text' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700',
                  )}>
                  {t('notificationTemplates.editor.textFallbackTab')}
                </button>
              </div>
              {editorTab === 'content' && (
                <button
                  onClick={isHtmlSourceMode ? handleHideHtmlSource : handleShowHtmlSource}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors',
                    isHtmlSourceMode
                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                  )}>
                  <Code className='h-3 w-3' />
                  {isHtmlSourceMode
                    ? t('notificationTemplates.editor.hideHtmlSource')
                    : t('notificationTemplates.editor.showHtmlSource')}
                </button>
              )}
            </div>

            {/* Content tab — structured editor or raw HTML */}
            {editorTab === 'content' && !isHtmlSourceMode && (
              <ContentEditor segments={segments} editedContents={segmentEdits} onChange={handleSegmentChange} />
            )}

            {editorTab === 'content' && isHtmlSourceMode && (
              <div className='space-y-2'>
                <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2'>
                  <AlertTriangle className='h-4 w-4 text-amber-600 mt-0.5 shrink-0' />
                  <p className='text-xs text-amber-800'>{t('notificationTemplates.editor.htmlSourceWarning')}</p>
                </div>
                <textarea
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  placeholder={t('notificationTemplates.editor.htmlPlaceholder')}
                  rows={20}
                  className='w-full rounded-md border border-amber-300 bg-amber-50/30 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y'
                />
              </div>
            )}

            {/* Text fallback tab */}
            {editorTab === 'text' && (
              <textarea
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                placeholder={t('notificationTemplates.editor.textPlaceholder')}
                rows={16}
                className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y'
              />
            )}
          </div>

          {/* Variables */}
          <VariablesPanel variables={template.variables} />
        </div>

        {/* Right — Preview */}
        <div className='flex-1 p-6'>
          <TemplatePreview preview={preview} isLoading={isPreviewLoading} onRefresh={() => void handlePreview()} />
        </div>
      </div>

      {/* Footer */}
      <div className='flex items-center justify-between border-t border-gray-200 px-6 py-4'>
        <div className='flex items-center gap-3'>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            {t('notificationTemplates.editor.active')}
          </label>
        </div>
        <div className='flex items-center gap-3'>
          <button
            onClick={() => setIsTestSendOpen(true)}
            className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md border border-gray-300'>
            {t('notificationTemplates.editor.testSend')}
          </button>
          <button
            onClick={onClose}
            className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md'>
            {t('notificationTemplates.editor.cancel')}
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={isSaving}
            className='inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50'>
            {isSaving && <Loader2 className='h-4 w-4 animate-spin' />}
            {t('notificationTemplates.editor.save')}
          </button>
        </div>
      </div>

      {/* Test Send Dialog */}
      <TestSendDialog
        isOpen={isTestSendOpen}
        isLoading={isTestSending}
        onClose={() => setIsTestSendOpen(false)}
        onSend={(email) => void handleTestSend(email)}
      />
    </div>
  );
}
